import React, { useMemo } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';
import {
  PhoneIcon,
  UsersIcon,
  ClipboardDocumentListIcon,
  ShoppingBagIcon,
  CurrencyDollarIcon,
  SparklesIcon,
} from '@ergeon/icons/hero/outline';
import { Card, Button, Tooltip, Badge, Link } from '@ergeon/core-components';

import { ROUTES } from '../../routes';
import { showUpcomingFeatures } from '../../utils';
import { useKeyboardShortcuts } from '../../hooks/useKeyboardShortcuts';
import { useUnifiedDialog } from '../../hooks/useUnifiedDialog';

const NAVIGATION_ITEMS = [
  {
    path: ROUTES.calls.$path(),
    label: 'Calls',
    Icon: PhoneIcon,
    matcher: (pathname: string) => pathname.startsWith('/calls'),
  },
  {
    path: ROUTES.customers.$path(),
    label: 'Customers',
    Icon: UsersIcon,
    matcher: (pathname: string) => pathname.startsWith('/customer'),
  },
  {
    path: ROUTES.orders.$path(),
    label: 'Orders',
    Icon: CurrencyDollarIcon,
    matcher: (pathname: string) => pathname.startsWith('/orders'),
    featureFlag: 'APPS-6502',
  },
  {
    path: ROUTES.activities.$path(),
    label: 'Activities',
    Icon: ClipboardDocumentListIcon,
    matcher: (pathname: string) => pathname.startsWith('/activities'),
  },
  {
    path: ROUTES.aiAgent.$path(),
    label: 'AI Agent',
    Icon: SparklesIcon,
    matcher: (pathname: string) => pathname.startsWith('/ai-agent'),
    featureFlag: 'APPS-6555',
  },
  {
    path: ROUTES.salesToolkit.$path(),
    label: 'Sales Toolkit',
    Icon: ShoppingBagIcon,
    matcher: (pathname: string) => pathname.startsWith('/sales-toolkit'),
  },
];

const Navigation: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { totalUnreadCount } = useUnifiedDialog();
  const getButtonFlavor = (isActive: boolean) => (isActive ? 'primary' : 'regular');
  const getButtonTaste = (isActive: boolean) => (isActive ? 'solid' : 'boundless');

  // Format count for display (show 99+ for counts over 99)
  const displayCount = totalUnreadCount > 99 ? '99+' : totalUnreadCount;

  // Show badge when there are unreads
  const showUnreadBadge = totalUnreadCount > 0;

  // Get visible navigation items (with feature flags)
  const visibleItems = useMemo(
    () => NAVIGATION_ITEMS.filter(({ featureFlag }) => !featureFlag || showUpcomingFeatures(featureFlag)),
    [],
  );

  // Register keyboard shortcuts for navigation (1-6)
  useKeyboardShortcuts({
    id: 'navigation',
    shortcuts: visibleItems.map((item, index) => ({
      key: String(index + 1),
      description: `Navigate to ${item.label}`,
      category: 'navigation',
      action: () => navigate(item.path),
    })),
  });

  return (
    <Card className="flex flex-col gap-2 p-2" zIndex="1">
      {visibleItems.map(({ path, label, Icon, matcher }, index) => {
        const isActive = matcher(location.pathname);
        const shortcutHint = ` (${index + 1})`;
        const isCustomersItem = label === 'Customers';
        return (
          <Link href={path} key={path} unstyled>
            <Tooltip msg={`${label}${shortcutHint}`} position="right">
              <Button flavor={getButtonFlavor(isActive)} isSquare size="medium" taste={getButtonTaste(isActive)}>
                <Icon aria-label={label} className="size-6" />
                {isCustomersItem && showUnreadBadge && (
                  <Badge className="-right-1 -bottom-1 absolute" flavor="warning" shape="circle" value={displayCount} />
                )}
              </Button>
            </Tooltip>
          </Link>
        );
      })}
    </Card>
  );
};

export default Navigation;
